'use client';

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@coas/ui/dialog';
import { cn } from '@coas/ui/lib';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@coas/ui/tabs';
import { Spinner } from '~/shared/ui/spinner';
import { useProductCategoriesDialog } from '../model/hook';
import { CategoryTree } from './CategoryTree';

interface ProductCategoriesDialogProps {
    products: ReadonlyArray<{ id: string }>;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const ProductCategoriesDialog = ({ products, open, onOpenChange }: ProductCategoriesDialogProps) => {
    const productIds = products.map(p => p.id);
    const vm = useProductCategoriesDialog(productIds, open);

    const blockCloseIfLoading = (e: Event) => {
        if (vm.isPending) e.preventDefault();
    };

    const submit = async () => {
        const ok = await vm.submit();
        if (ok) onOpenChange(false);
    };

    const canApplyOnMan = vm.counts.male + vm.counts.unisex > 0;
    const canApplyOnWoman = vm.counts.female + vm.counts.unisex > 0;

    return (
        <Dialog
            open={open || vm.isPending}
            onOpenChange={next => {
                if (vm.isPending) return;
                onOpenChange(next);
            }}
        >
            <DialogContent
                className={cn(
                    'z-[100] w-[min(92vw,800px)] rounded-12 border border-neutral-200 bg-neutral-50 text-neutral-950 shadow-lg outline-none',
                    'max-h-[min(80vh,calc(var(--visual-viewport-height)-2rem))] overflow-hidden',
                    'p-0',
                    '[&>button]:right-4 [&>button]:top-4 md:[&>button]:right-6 md:[&>button]:top-6',
                    '[&>button]:z-10',
                )}
                onEscapeKeyDown={blockCloseIfLoading}
                onPointerDownOutside={blockCloseIfLoading}
                onInteractOutside={blockCloseIfLoading}
            >
                <div className='flex max-h-full min-h-0 flex-col gap-4 px-4 py-4 md:px-6 md:py-6'>
                    <DialogHeader className='text-left pr-10'>
                        <div className='flex flex-col gap-2'>
                            <DialogTitle className='text-18 font-600 text-neutral-950 md:text-22'>
                                Массовое управление категориями
                            </DialogTitle>

                            <DialogDescription
                                className='text-left text-16 leading-snug text-neutral-500 md:text-18'
                                aria-live='polite'
                            >
                                <span className='sm:hidden'>
                                    Выбрано: {productIds.length} · М: {vm.counts.male} · Ж: {vm.counts.female} · У:{' '}
                                    {vm.counts.unisex}
                                </span>
                                <span className='hidden sm:inline'>
                                    Выбранных товаров: {productIds.length}. Мужских: {vm.counts.male} · Женских:{' '}
                                    {vm.counts.female} · Унисекс: {vm.counts.unisex}
                                </span>
                            </DialogDescription>

                            <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-13 text-neutral-500 md:text-14'>
                                <span className='inline-flex items-center gap-1.5'>
                                    <span
                                        className='inline-block h-2 w-2 rounded-full bg-emerald-500'
                                        aria-hidden='true'
                                    />
                                    <span>
                                        <span className='hidden sm:inline'>все товары</span>
                                        <span className='sm:hidden'>все</span>
                                    </span>
                                </span>

                                <span className='inline-flex items-center gap-1.5'>
                                    <span
                                        className='inline-block h-2 w-2 rounded-full bg-amber-500'
                                        aria-hidden='true'
                                    />
                                    <span>
                                        <span className='hidden sm:inline'>часть товаров</span>
                                        <span className='sm:hidden'>часть</span>
                                    </span>
                                </span>

                                <span className='inline-flex items-center gap-1.5'>
                                    <span
                                        className='inline-block h-2 w-2 rounded-full bg-neutral-300'
                                        aria-hidden='true'
                                    />
                                    <span>
                                        <span className='hidden sm:inline'>нет товаров</span>
                                        <span className='sm:hidden'>нет</span>
                                    </span>
                                </span>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className='flex min-h-0 flex-1 flex-col gap-4'>
                        <Tabs defaultValue='man' className='flex min-h-0 flex-1 flex-col overflow-hidden'>
                            <TabsList className='grid h-12 w-full grid-cols-2 rounded-10 border border-neutral-200 bg-neutral-100 p-1 text-neutral-600'>
                                <TabsTrigger
                                    value='man'
                                    className={cn(
                                        'h-full w-full rounded-8 border border-transparent transition-colors',
                                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/20',
                                        'hover:bg-white/60 hover:text-neutral-900',
                                        'data-[state=active]:border-neutral-200 data-[state=active]:bg-white data-[state=active]:text-neutral-950',
                                        'data-[state=active]:shadow-sm',
                                    )}
                                >
                                    Мужские
                                </TabsTrigger>

                                <TabsTrigger
                                    value='woman'
                                    className={cn(
                                        'h-full w-full rounded-8 border border-transparent transition-colors',
                                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/20',
                                        'hover:bg-white/60 hover:text-neutral-900',
                                        'data-[state=active]:border-neutral-200 data-[state=active]:bg-white data-[state=active]:text-neutral-950',
                                        'data-[state=active]:shadow-sm',
                                    )}
                                >
                                    Женские
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value='man' className='mt-3 min-h-0 flex-1 overflow-hidden'>
                                {vm.isLoadingCats || vm.isLoadingInd ? (
                                    <div className='grid place-items-center py-10'>
                                        <Spinner className='size-6 text-neutral-500' />
                                    </div>
                                ) : canApplyOnMan ? (
                                    <CategoryTree
                                        items={vm.male.categories}
                                        statusById={vm.male.statusById}
                                        actions={vm.male.actions}
                                        onAction={(id, next) => vm.onChange('man', id, next)}
                                        disabled={vm.isPending}
                                        className='bg-white'
                                    />
                                ) : (
                                    <div className='grid place-items-center rounded-10 border border-neutral-200 bg-neutral-50 px-4 py-8 text-center'>
                                        <div className='max-w-[520px] text-12 text-neutral-600 sm:text-13'>
                                            Для мужской вкладки нет подходящих товаров. Требуются товары с полом{' '}
                                            <span className='font-600 text-neutral-800'>Мужской</span> или{' '}
                                            <span className='font-600 text-neutral-800'>Унисекс</span>.
                                        </div>
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value='woman' className='mt-3 min-h-0 flex-1 overflow-hidden'>
                                {vm.isLoadingCats || vm.isLoadingInd ? (
                                    <div className='grid place-items-center py-10'>
                                        <Spinner className='size-6 text-neutral-500' />
                                    </div>
                                ) : canApplyOnWoman ? (
                                    <CategoryTree
                                        items={vm.female.categories}
                                        statusById={vm.female.statusById}
                                        actions={vm.female.actions}
                                        onAction={(id, next) => vm.onChange('woman', id, next)}
                                        disabled={vm.isPending}
                                        className='bg-white'
                                    />
                                ) : (
                                    <div className='grid place-items-center rounded-10 border border-neutral-200 bg-neutral-50 px-4 py-8 text-center'>
                                        <div className='max-w-[520px] text-12 text-neutral-600 sm:text-13'>
                                            Для женской вкладки нет подходящих товаров. Требуются товары с полом{' '}
                                            <span className='font-600 text-neutral-800'>Женский</span> или{' '}
                                            <span className='font-600 text-neutral-800'>Унисекс</span>.
                                        </div>
                                    </div>
                                )}
                            </TabsContent>
                        </Tabs>

                        <DialogFooter className='p-0'>
                            <div className='flex flex-row items-center justify-end gap-2 sm:gap-3'>
                                <DialogClose asChild>
                                    <button
                                        type='button'
                                        onClick={() => onOpenChange(false)}
                                        disabled={vm.isPending}
                                        className={cn(
                                            'h-9 px-3 text-13 md:px-4 md:text-14',
                                            'rounded-8 font-500 shrink-0',
                                            'bg-neutral-200 text-neutral-900',
                                            'transition-colors duration-150 hover:bg-neutral-300',
                                            'disabled:pointer-events-none disabled:opacity-60',
                                        )}
                                    >
                                        Отмена
                                    </button>
                                </DialogClose>

                                <button
                                    type='button'
                                    onClick={submit}
                                    disabled={!vm.canSubmit}
                                    className={cn(
                                        'h-9 px-3 text-13 md:px-4 md:text-14',
                                        'rounded-8 font-700 shrink-0',
                                        'bg-neutral-950 text-white',
                                        'transition-opacity duration-150',
                                        'disabled:pointer-events-none disabled:opacity-60',
                                        vm.isPending && 'animate-pulse',
                                    )}
                                >
                                    Сохранить
                                </button>
                            </div>
                        </DialogFooter>
                    </div>
                </div>

                {vm.isPending && (
                    <div className='absolute inset-0 z-50 grid place-items-center rounded-12 bg-neutral-50/70'>
                        <Spinner className='size-6' />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
